import type {
  Association,
  Pupil,
  RefreshRun,
  Selection,
  StoredFixture,
  StoredTeam,
} from "../shared/contracts";

const DB_NAME = "fettes-fixtures";
const DB_VERSION = 1;
const STORES = ["pupils", "teams", "associations", "fixtures", "selections", "refreshRuns"] as const;
type StoreName = (typeof STORES)[number];

export type Snapshot = {
  pupils: Pupil[];
  teams: StoredTeam[];
  associations: Association[];
  fixtures: StoredFixture[];
  selections: Selection[];
  refreshRuns: RefreshRun[];
};

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed"));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error("IndexedDB transaction failed"));
    transaction.onabort = () => reject(transaction.error ?? new Error("IndexedDB transaction aborted"));
  });
}

export function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      for (const store of STORES) {
        if (!database.objectStoreNames.contains(store)) database.createObjectStore(store, { keyPath: "id" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Unable to open local database"));
  });
}

export async function loadSnapshot(database: IDBDatabase): Promise<Snapshot> {
  const transaction = database.transaction(STORES, "readonly");
  const results = await Promise.all(STORES.map((name) => requestResult(transaction.objectStore(name).getAll())));
  await transactionDone(transaction);
  return Object.fromEntries(STORES.map((name, index) => [name, results[index]])) as Snapshot;
}

export async function commitChanges(
  database: IDBDatabase,
  changes: Partial<Snapshot>,
  removals: Partial<Record<StoreName, string[]>> = {},
): Promise<void> {
  const names = [...new Set([
    ...(Object.keys(changes) as StoreName[]),
    ...(Object.keys(removals) as StoreName[]),
  ])].filter((name) => STORES.includes(name));
  if (names.length === 0) return;
  const transaction = database.transaction(names, "readwrite");
  for (const name of names) {
    const store = transaction.objectStore(name);
    for (const id of removals[name] ?? []) store.delete(id);
    for (const value of changes[name] ?? []) store.put(value);
  }
  await transactionDone(transaction);
}

export async function clearDatabase(database: IDBDatabase): Promise<void> {
  const transaction = database.transaction(STORES, "readwrite");
  for (const store of STORES) transaction.objectStore(store).clear();
  await transactionDone(transaction);
}

export async function removePupil(database: IDBDatabase, pupilId: string): Promise<void> {
  const snapshot = await loadSnapshot(database);
  const removedAssociations = snapshot.associations.filter((item) => item.pupilId === pupilId);
  const removedTeamIds = new Set(removedAssociations.map((item) => item.teamId));
  const remainingTeamIds = new Set(snapshot.associations.filter((item) => item.pupilId !== pupilId).map((item) => item.teamId));
  const orphanedTeamIds = new Set([...removedTeamIds].filter((teamId) => !remainingTeamIds.has(teamId)));
  const orphanedFixtureIds = new Set(snapshot.fixtures.filter((item) => orphanedTeamIds.has(item.teamId)).map((item) => item.id));
  const transaction = database.transaction(["pupils", "associations", "selections", "teams", "fixtures"], "readwrite");
  transaction.objectStore("pupils").delete(pupilId);
  for (const association of removedAssociations) transaction.objectStore("associations").delete(association.id);
  for (const selection of snapshot.selections) {
    if (selection.pupilId === pupilId || orphanedFixtureIds.has(selection.fixtureId)) transaction.objectStore("selections").delete(selection.id);
  }
  for (const teamId of orphanedTeamIds) transaction.objectStore("teams").delete(teamId);
  for (const fixtureId of orphanedFixtureIds) transaction.objectStore("fixtures").delete(fixtureId);
  await transactionDone(transaction);
}
